import ReplayIcon from '@mui/icons-material/Replay';
import Button from '@mui/material/Button/Button';
import React from 'react';
import { Link } from 'react-router-dom';
import { HackathonPayload } from '../../services/hackathon.service';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Card from '@mui/material/Card/Card';
import CardActionArea from '@mui/material/CardActionArea/CardActionArea';
import CardContent from '@mui/material/CardContent/CardContent';
import Chip from '@mui/material/Chip/Chip';
import { useTheme } from '@mui/material/styles';
import EventIcon from '@mui/icons-material/Event';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { BrandedThemeProvider } from '../../components/Theme/AppThemeProvider';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '../../services/useAuth';
import { formatDateRange, getHackathonStatus, lastActivity, myPart } from '../../utils/hackathonStatus';

dayjs.extend(relativeTime);

const SummaryCard = ({ hackathon }: { hackathon: HackathonPayload }) => {
    const theme = useTheme();
    const status = getHackathonStatus(hackathon);
    const { bannerUrl, logoUrl, gradient } = theme.brand;
    const dates = formatDateRange(hackathon.startDate, hackathon.endDate);
    const { user } = useAuth();
    const part = myPart(hackathon, user ? user.email : undefined);
    const activity = lastActivity(hackathon);
    const canRunAgain = !!user && hackathon.organizers.some(o => o.email === user.email) && ['ended', 'results'].includes(status.phase);

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardActionArea component={Link} to={`/h/${hackathon.slug}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', color: 'text.primary', textDecoration: 'none' }}>
                <Box sx={{
                    height: 88,
                    background: bannerUrl ? `url("${bannerUrl.replace(/"/g, '%22')}") center / cover no-repeat` : gradient,
                    position: 'relative'
                }}>
                    {
                        logoUrl && (
                            <Box sx={{
                                position: 'absolute',
                                left: 16,
                                bottom: -20,
                                backgroundColor: '#fff',
                                borderRadius: '10px',
                                p: 0.75,
                                display: 'flex',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                            }}>
                                <Box component="img" src={logoUrl} alt="" sx={{ height: 28, maxWidth: 96, objectFit: 'contain' }} />
                            </Box>
                        )
                    }
                </Box>
                <CardContent sx={{ pt: logoUrl ? 4 : 2, flex: 1 }}>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                            <Typography variant="h5" component="h3" sx={{ overflowWrap: 'anywhere' }}>{hackathon.name}</Typography>
                            <Chip size="small" label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} sx={{ flexShrink: 0 }} />
                        </Stack>
                        {
                            (hackathon.organization || hackathon.resolvedBranding.displayName) && (
                                <Typography variant="body2" color="text.secondary">{hackathon.resolvedBranding.displayName || hackathon.organization?.name}</Typography>
                            )
                        }
                        {part && <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>{part}</Typography>}
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ color: 'text.secondary' }}>
                            {
                                dates && (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <EventIcon fontSize="small" />
                                        <Typography variant="body2">{dates}</Typography>
                                    </Stack>
                                )
                            }
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <PeopleOutlineIcon fontSize="small" />
                                <Typography variant="body2">{hackathon.participants.length}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <LightbulbOutlinedIcon fontSize="small" />
                                <Typography variant="body2">{hackathon.ideas.filter(i => !i.archived).length}</Typography>
                            </Stack>
                        </Stack>
                        {activity && <Typography variant="caption" color="text.secondary">Last activity {dayjs(activity).fromNow()}</Typography>}
                    </Stack>
                </CardContent>
            </CardActionArea>
            {
                // outside the card's link: a finished event you ran can be started again from here
                canRunAgain && <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
                    <Button size="small" startIcon={<ReplayIcon />} component={Link} to={`/new?from=${hackathon._id}`}
                        aria-label={`Run it again: ${hackathon.name}`}>Run it again</Button>
                </Box>
            }
        </Card>
    )
}

/**
 * A hackathon card shown in its own branding.
 */
export const HackathonSummary = ({
    hackathon
}: {
    hackathon: HackathonPayload
}) => {
    return (
        <BrandedThemeProvider branding={hackathon.resolvedBranding}>
            <SummaryCard hackathon={hackathon} />
        </BrandedThemeProvider>
    )
}
